import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, Facebook, Instagram } from 'lucide-react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { business, navigation } = useSiteSettings();

  const quickLinks = (navigation || [])
    .filter((n) => n.visible)
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  const addressCombined = [business?.address_line1, business?.address_line2].filter(Boolean).join(', ');

  return (
    <footer className="bg-slate-950 border-t border-gray-400/20" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{business?.name}</h3>
            <p className="text-gray-400 text-sm leading-relaxed">{business?.tagline}</p>
            <p className="text-gray-400 text-sm mt-4">Proudly supporting our 2nd Amendment.</p>
            {(business?.facebook_url || business?.instagram_url) && (
              <div className="flex items-center gap-3 mt-4">
                {business?.facebook_url && (
                  <a href={business.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-gray-400 hover:text-white transition-colors">
                    <Facebook className="w-5 h-5" />
                  </a>
                )}
                {business?.instagram_url && (
                  <a href={business.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-gray-400 hover:text-white transition-colors">
                    <Instagram className="w-5 h-5" />
                  </a>
                )}
              </div>
            )}
          </div>

          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Contact</h3>
            <div className="space-y-3">
              <a href={`tel:${business?.phone || ''}`} className="flex items-start space-x-3 text-gray-400 hover:text-gray-100 transition-colors group" data-testid="footer-phone">
                <Phone className="w-5 h-5 mt-0.5 group-hover:scale-110 transition-transform" />
                <span className="text-sm">{business?.phone}</span>
              </a>
              <a href={`mailto:${business?.email || ''}`} className="flex items-start space-x-3 text-gray-400 hover:text-gray-100 transition-colors group" data-testid="footer-email">
                <Mail className="w-5 h-5 mt-0.5 group-hover:scale-110 transition-transform" />
                <span className="text-sm">{business?.email}</span>
              </a>
              <div className="flex items-start space-x-3 text-gray-400" data-testid="footer-address">
                <MapPin className="w-5 h-5 mt-0.5" />
                <span className="text-sm">{addressCombined}</span>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Hours</h3>
            <div className="space-y-2">
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
                <div className="text-sm">
                  <p className="text-gray-400">{business?.hours_weekday}</p>
                  <p className="text-gray-400 mt-1">{business?.hours_saturday}</p>
                  <p className="text-gray-400 mt-1">{business?.hours_sunday}</p>
                </div>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold text-lg mb-4">Quick Links</h3>
            <div className="space-y-2">
              {quickLinks.map((n) => {
                const isRoute = n.path?.startsWith('/') && !n.path?.startsWith('/#');
                return isRoute ? (
                  <Link key={n.key} to={n.path} className="block text-gray-400 hover:text-gray-100 text-sm transition-colors">{n.label}</Link>
                ) : (
                  <a key={n.key} href={n.path?.startsWith('/') ? n.path.slice(1) : n.path} className="block text-gray-400 hover:text-gray-100 text-sm transition-colors">{n.label}</a>
                );
              })}
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">© {currentYear} {business?.name}. All rights reserved.</p>
            <div className="flex space-x-6">
              <a href="#" className="text-gray-400 hover:text-gray-100 text-sm transition-colors">Privacy Policy</a>
              <a href="#" className="text-gray-400 hover:text-gray-100 text-sm transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
